/**
 * Workflow Execution Engine
 * 
 * Shared logic used by:
 *  - Frontend triggers (store.js) for instant execution
 *  - Cron job (/api/cron/workflows.js) for delayed steps
 * 
 * This module runs in the BROWSER (frontend triggers).
 * The cron has its own server-side copy of executeAction.
 */
import supabase from './supabase';
import apiFetch from './api';

// ── Parse delay strings into milliseconds ──
const DELAY_MS = {
  "5 minutes": 5*60*1000, "15 minutes": 15*60*1000, "30 minutes": 30*60*1000,
  "1 hour": 60*60*1000, "4 hours": 4*60*60*1000,
  "1 day": 24*60*60*1000, "2 days": 2*24*60*60*1000, "3 days": 3*24*60*60*1000,
  "5 days": 5*24*60*60*1000, "1 week": 7*24*60*60*1000,
  "2 weeks": 14*24*60*60*1000, "1 month": 30*24*60*60*1000,
};

/**
 * Check if any active workflows match a trigger event,
 * and fire them for the given contact.
 * 
 * Called from store.js after contact/deal actions.
 */
export async function checkAndFireWorkflows(triggerType, context = {}) {
  const orgId = window.__coreflow_org_id;
  if (!orgId) return;

  try {
    // Load active workflows matching this trigger
    const { data: workflows } = await supabase.from("workflows")
      .select("*")
      .eq("trigger_type", triggerType)
      .eq("status", "active");

    if (!workflows || workflows.length === 0) return;

    for (const wf of workflows) {
      // Check trigger config filters
      if (!matchesTriggerConfig(wf.trigger_config, triggerType, context)) continue;

      // Check if contact is already enrolled OR was recently enrolled (idempotency guard)
      const recentCutoff = new Date(Date.now() - 60000).toISOString();
      const { data: existing } = await supabase.from("workflow_queue")
        .select("id")
        .eq("workflow_id", wf.id)
        .eq("contact_id", context.contactId)
        .or(`status.in.(waiting,running),created_at.gte.${recentCutoff}`)
        .limit(1);

      if (existing && existing.length > 0) continue; // already enrolled or recently fired

      // Enroll and start executing
      console.log(`[CoreFlow WF] ⚡ Firing "${wf.name}" for contact ${context.contactId}`);
      await executeWorkflow(wf, context);
    }
  } catch (e) {
    console.warn("[CoreFlow WF] Trigger check failed:", e.message);
  }
}

/**
 * Check if trigger config filters match the event context
 */
function matchesTriggerConfig(config, triggerType, context) {
  if (!config || Object.keys(config).length === 0) return true;

  switch (triggerType) {
    case "deal_stage":
      if (config.from_stage && config.from_stage !== "Any" && config.from_stage !== context.fromStage) return false;
      if (config.to_stage && config.to_stage !== "Any" && config.to_stage !== context.toStage) return false;
      return true;
    case "contact_added":
      if (config.source && config.source !== "Any" && config.source !== context.source) return false;
      return true;
    case "score_threshold":
      const score = context.score || 0;
      const threshold = parseInt(config.value) || 0;
      if (config.operator === "is above" && score <= threshold) return false;
      if (config.operator === "is below" && score >= threshold) return false;
      if (config.operator === "equals" && score !== threshold) return false;
      return true;
    case "call_completed":
      if (config.min_duration && (context.duration || 0) < parseInt(config.min_duration)) return false;
      if (config.outcome && config.outcome !== "Any" && config.outcome !== context.outcome) return false;
      return true;
    case "email_sent":
      if (config.subject_contains && !((context.subject || "").toLowerCase().includes(config.subject_contains.toLowerCase()))) return false;
      return true;
    case "email_opened":
    case "email_clicked":
    case "email_replied":
      return true;
    case "sms_sent":
    case "sms_received":
      return true;
    case "meeting_booked":
      if (config.meeting_type && config.meeting_type !== "Any" && config.meeting_type !== context.meetingType) return false;
      return true;
    case "form_submitted":
      if (config.form_id && config.form_id !== context.formId) return false;
      return true;
    default:
      return true;
  }
}

/**
 * Execute a workflow for a contact.
 * Runs immediate steps in sequence, queues delayed steps for cron.
 */
async function executeWorkflow(workflow, context) {
  const orgId = window.__coreflow_org_id;
  const contact = context.contact || {};
  const contactId = context.contactId || contact.id;
  const contactName = [contact.fn || contact.first_name, contact.ln || contact.last_name].filter(Boolean).join(" ") || "Unknown";

  // Create execution log
  let logId = null;
  try {
    const { data: log } = await supabase.from("workflow_logs").insert({
      org_id: orgId,
      workflow_id: workflow.id,
      contact_id: contactId,
      contact_name: contactName,
      status: "running",
      is_test: false,
      steps_executed: [],
      created_by: window.__coreflow_user_id || null,
    }).select().single();
    logId = log?.id;
  } catch (e) { console.warn("[CoreFlow WF] Log create failed:", e.message); }

  // Execute steps sequentially
  const stepsLog = [];
  let hasError = false;

  try {
    const result = await executeStepsList(workflow.steps || [], contact, context, orgId, contactId, logId, stepsLog);
    hasError = result.hasError;
  } catch (e) {
    hasError = true;
    stepsLog.push({ action: "error", label: "Execution error", status: "failed", message: e.message, timestamp: new Date().toISOString() });
  }

  // Update log
  if (logId) {
    try {
      await supabase.from("workflow_logs").update({
        status: hasError ? "failed" : "completed",
        steps_executed: stepsLog,
        error_message: hasError ? stepsLog.filter(s => s.status === "failed").map(s => s.message).join(" | ") : null,
        error_step: hasError ? stepsLog.find(s => s.status === "failed")?.label : null,
        completed_at: new Date().toISOString(),
      }).eq("id", logId);
    } catch (e) {}
  }

  // Update workflow stats
  try {
    await supabase.from("workflows").update({
      last_run_at: new Date().toISOString(),
      run_count: (workflow.run_count || 0) + 1,
      error_count: hasError ? (workflow.error_count || 0) + 1 : workflow.error_count || 0,
    }).eq("id", workflow.id);
  } catch (e) {}
}

/**
 * Execute a list of steps sequentially.
 * Returns { hasError: boolean }
 */
async function executeStepsList(steps, contact, context, orgId, contactId, logId, stepsLog) {
  let hasError = false;

  for (const step of steps) {
    // Handle delays — queue for cron instead of blocking
    if (step.type === "delay") {
      const delayMs = DELAY_MS[step.config?.amount] || 0;
      if (delayMs > 60000) { // More than 1 minute → queue for cron
        try {
          await supabase.from("workflow_queue").insert({
            org_id: orgId,
            workflow_id: context.workflowId || null,
            contact_id: contactId,
            log_id: logId,
            current_step_id: step.id,
            status: "waiting",
            next_run_at: new Date(Date.now() + delayMs).toISOString(),
            context: { ...context, remainingSteps: getStepsAfter(steps, step.id), contact },
          });
        } catch (e) { console.warn("[CoreFlow WF] Queue insert failed:", e.message); }

        stepsLog.push({
          stepId: step.id, action: step.action, label: step.label, type: "delay",
          status: "queued", message: `Queued: wait ${step.config?.amount}, will resume via cron`,
          timestamp: new Date().toISOString(),
        });
        return { hasError }; // Stop here — cron will pick up the rest
      } else {
        // Short delay — just wait
        stepsLog.push({
          stepId: step.id, action: step.action, label: step.label, type: "delay",
          status: "passed", message: `Waited ${step.config?.amount}`,
          timestamp: new Date().toISOString(),
        });
        continue;
      }
    }

    // Handle conditions — evaluate and run the matching branch
    if (step.type === "condition") {
      const result = evaluateCondition(step, contact, context);
      stepsLog.push({
        stepId: step.id, action: step.action, label: step.label, type: "condition",
        status: "passed", message: `Evaluated: ${result ? "YES" : "NO"} branch`,
        timestamp: new Date().toISOString(),
      });

      const branch = result ? (step.yes || []) : (step.no || []);
      if (branch.length > 0) {
        const branchResult = await executeStepsList(branch, contact, context, orgId, contactId, logId, stepsLog);
        if (branchResult.hasError) hasError = true;
      }
      continue;
    }

    // Execute action step
    try {
      const msg = await executeAction(step, contact, context, orgId, contactId);
      
      // Update local contact object after state-changing actions
      // so subsequent conditions in this workflow see the new values
      const cfg = step.config || {};
      if (step.action === "update_property" && (!cfg.entity || cfg.entity === "contact")) {
        const prop = cfg.property;
        const propLower = (prop || "").toLowerCase();
        const knownMap = { "lead stage": "leadStage", "status": "status", "score": "score", "source": "source", "industry": "industry", "account type": "type", "city": "city", "state": "state" };
        if (knownMap[propLower]) {
          contact[knownMap[propLower]] = propLower === "score" ? (parseInt(cfg.value) || 0) : cfg.value;
        } else {
          contact[prop] = cfg.value;
          if (!contact.properties) contact.properties = {};
          contact.properties[prop] = cfg.value;
        }
      }
      if (step.action === "add_score") {
        const points = parseInt(cfg.points) || 0;
        if (cfg.operation?.includes("Add")) contact.score = (contact.score || 0) + points;
        else if (cfg.operation?.includes("Subtract")) contact.score = Math.max(0, (contact.score || 0) - points);
        else contact.score = points;
      }
      if (step.action === "add_tag") {
        if (!contact.tags) contact.tags = [];
        if (!contact.tags.includes(cfg.tag)) contact.tags.push(cfg.tag);
      }
      if (step.action === "remove_tag") {
        contact.tags = (contact.tags || []).filter(t => t !== cfg.tag);
      }
      if (step.action === "change_stage" && context.deal) {
        context.deal.stage = cfg.stage;
        context.toStage = cfg.stage;
        context.dealStage = cfg.stage;
      }
      
      stepsLog.push({
        stepId: step.id, action: step.action, label: step.label, type: "action",
        status: "passed", message: msg,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      hasError = true;
      stepsLog.push({
        stepId: step.id, action: step.action, label: step.label, type: "action",
        status: "failed", message: e.message,
        timestamp: new Date().toISOString(),
      });
    }
  }

  return { hasError };
}

/**
 * Get remaining steps after a given step ID (for queueing)
 */
function getStepsAfter(steps, afterId) {
  const idx = steps.findIndex(s => s.id === afterId);
  return idx >= 0 ? steps.slice(idx + 1) : [];
}

/**
 * Evaluate a condition step. Returns true (yes branch) or false (no branch).
 */
function evaluateCondition(step, contact, context) {
  const cfg = step.config || {};
  const field = cfg.field;
  const op = cfg.operator;
  const val = cfg.value;

  // A/B split
  if (step.action === "split") {
    const pct = parseInt(cfg.split_pct) || 50;
    return Math.random() * 100 < pct;
  }

  // Get the actual value to compare
  let actual = null;
  switch (field) {
    case "Email opened": actual = context.emailOpened ? "true" : "false"; break;
    case "Email clicked": actual = context.emailClicked ? "true" : "false"; break;
    case "Email replied": actual = context.emailReplied ? "true" : "false"; break;
    case "Deal value": actual = String(context.dealValue || contact.dealValue || 0); break;
    case "Contact score": actual = String(contact.score || 0); break;
    case "Lead stage": actual = contact.leadStage || ""; break;
    case "Status": actual = contact.status || ""; break;
    case "Has tag": {
      const hasIt = (contact.tags || []).includes(val);
      if (op === "equals" || op === "is set") return hasIt;
      if (op === "not equals" || op === "is not set") return !hasIt;
      return hasIt;
    }
    case "Deal stage": actual = context.dealStage || context.toStage || ""; break;
    case "Days since last activity": actual = String(context.daysSinceActivity || 0); break;
    case "Task completed": actual = context.taskCompleted ? "true" : "false"; break;
    default: {
      // Check contact fields (top-level, then properties)
      const fromContact = contact[field] ?? contact.properties?.[field];
      if (fromContact !== undefined && fromContact !== null) { actual = String(fromContact); break; }
      // Check deal fields from context
      const deal = context.deal || {};
      const fromDeal = deal[field] ?? deal.properties?.[field];
      if (fromDeal !== undefined && fromDeal !== null) { actual = String(fromDeal); break; }
      // Check company fields from context
      const company = context.company || {};
      const fromCompany = company[field] ?? company.properties?.[field];
      if (fromCompany !== undefined && fromCompany !== null) { actual = String(fromCompany); break; }
      // Check raw context
      if (context[field] !== undefined) { actual = String(context[field]); break; }
      actual = "";
      break;
    }
  }

  switch (op) {
    case "equals": return actual === val;
    case "not equals": return actual !== val;
    case "greater than": return parseFloat(actual) > parseFloat(val);
    case "less than": return parseFloat(actual) < parseFloat(val);
    case "contains": return actual.toLowerCase().includes((val || "").toLowerCase());
    case "is set": return actual && actual !== "" && actual !== "false";
    case "is not set": return !actual || actual === "" || actual === "false";
    default: return false;
  }
}

/**
 * Execute a single action step. Returns a success message.
 * Throws on failure.
 */
async function executeAction(step, contact, context, orgId, contactId) {
  const cfg = step.config || {};
  const contactName = [contact.fn || contact.first_name, contact.ln || contact.last_name].filter(Boolean).join(" ");
  const contactEmail = contact.email;

  // Merge template fields
  const merge = (text) => {
    let result = (text || "")
      .replace(/\{\{first_name\}\}/gi, contact.fn || contact.first_name || "")
      .replace(/\{\{last_name\}\}/gi, contact.ln || contact.last_name || "")
      .replace(/\{\{full_name\}\}/gi, contactName)
      .replace(/\{\{company\}\}/gi, contact.co || contact.company || "")
      .replace(/\{\{email\}\}/gi, contactEmail || "")
      .replace(/\{\{phone\}\}/gi, contact.ph || contact.phone || "")
      .replace(/\{\{contact_name\}\}/gi, contactName)
      .replace(/\{\{deal_name\}\}/gi, context.dealName || "")
      .replace(/\{\{deal_value\}\}/gi, String(context.dealValue || ""))
      .replace(/\{\{deal_stage\}\}/gi, context.toStage || context.dealStage || "")
      .replace(/\{\{trigger_reason\}\}/gi, context.triggerReason || "Workflow triggered");
    // Replace any remaining {{field_name}} with contact/deal/company properties
    result = result.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return String(contact[key] ?? contact.properties?.[key] ?? (context.deal || {})[key] ?? (context.deal?.properties || {})[key] ?? (context.company || {})[key] ?? match);
    });
    return result;
  };

  switch (step.action) {
    case "send_email": {
      if (!contactEmail) throw new Error("Contact has no email address");
      const resp = await apiFetch("/api/email/send", {
        method: "POST",
        body: JSON.stringify({
          to: contactEmail,
          subject: merge(cfg.subject || ""),
          body: merge(cfg.body || ""),
          contactId, orgId,
          userId: window.__coreflow_user_id,
        }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || "Email send failed");
      return `Sent email to ${contactEmail}: "${cfg.subject || ""}"`;
    }

    case "send_sms": {
      const phone = contact.ph || contact.phone;
      if (!phone) throw new Error("Contact has no phone number");
      const resp = await apiFetch("/api/sms/send", {
        method: "POST",
        body: JSON.stringify({
          to: phone, body: merge(cfg.message || ""),
          contactId, orgId,
        }),
      });
      const data = await resp.json();
      if (!data.success && !data.sid) throw new Error(data.error || "SMS send failed");
      return `Sent SMS to ${phone}`;
    }

    case "create_task": {
      await supabase.from("tasks").insert({
        org_id: orgId,
        title: merge(cfg.title || "Follow up"),
        contact_id: contactId,
        priority: (cfg.priority || "medium").toLowerCase(),
        due_date: cfg.due ? new Date(Date.now() + parseDueDays(cfg.due)).toISOString() : null,
        status: "pending",
        assigned_to: context.ownerId || window.__coreflow_user_id || null,
      });
      return `Created task: "${merge(cfg.title)}"`;
    }

    case "update_property": {
      const prop = cfg.property;
      const propLower = (prop || "").toLowerCase();
      const entity = cfg.entity || "contact";
      
      // Known column mappings per entity
      const knownColumns = {
        contact: { "lead stage": "lead_stage", "status": "status", "score": "score", "source": "lead_source", "industry": "industry", "account type": "account_type", "city": "city", "state": "state" },
        deal: { "stage": "stage", "probability": "probability", "close date": "close_date", "name": "name", "value": "value" },
        task: { "title": "title", "priority": "priority", "due date": "due_date", "status": "status", "assigned to": "assigned_to" },
        call: { "disposition": "disposition", "duration": "duration", "notes": "notes" },
        email: { "subject": "subject", "status": "status" },
        sms: { "status": "status" },
        calendar: { "title": "title", "location": "location" },
      };
      
      const tableMap = {
        contact: "contacts", deal: "deals", task: "tasks", call: "call_logs",
        email: "emails", sms: "sms_messages", calendar: "calendar_events",
      };
      
      const idMap = {
        contact: contactId, deal: context.dealId, task: context.taskId,
        call: context.callId, email: context.emailId, sms: context.smsId,
        calendar: context.eventId,
      };
      
      const table = tableMap[entity] || "contacts";
      const recordId = idMap[entity] || (entity === "contact" ? contactId : null);
      const known = knownColumns[entity] || {};
      
      if (!recordId) throw new Error(`No ${entity} ID available for property update`);
      
      if (known[propLower]) {
        const colVal = propLower === "score" ? (parseInt(cfg.value) || 0) : cfg.value;
        await supabase.from(table).update({ [known[propLower]]: colVal }).eq("id", recordId);
      } else {
        // Custom field — merge into properties JSON
        try {
          const { data: existing } = await supabase.from(table).select("properties").eq("id", recordId).single();
          const props = { ...(existing?.properties || {}), [prop]: cfg.value };
          const { error } = await supabase.from(table).update({ properties: props }).eq("id", recordId);
          if (error) throw error;
        } catch (e) {
          // If properties column doesn't exist, try writing to a known fallback
          console.warn(`[CoreFlow WF] Properties update failed on ${table}:`, e.message);
          throw new Error(`Cannot set custom property "${prop}" on ${entity} — properties column may not exist. Run the Supabase migration.`);
        }
      }
      return `Set ${entity} ${prop} = "${cfg.value}"`;
    }

    case "add_score": {
      const points = parseInt(cfg.points) || 0;
      const currentScore = contact.score || 0;
      let newScore;
      if (cfg.operation?.includes("Add")) newScore = currentScore + points;
      else if (cfg.operation?.includes("Subtract")) newScore = Math.max(0, currentScore - points);
      else newScore = points;
      
      await supabase.from("contacts").update({ score: newScore }).eq("id", contactId);
      return `Score: ${currentScore} → ${newScore}`;
    }

    case "change_stage": {
      const dealId = context.dealId || contact.dealId;
      if (!dealId) throw new Error("No deal associated with contact");
      await supabase.from("deals").update({ stage: cfg.stage }).eq("id", dealId);
      return `Moved deal to "${cfg.stage}"`;
    }

    case "add_tag": {
      const currentTags = contact.tags || [];
      if (!currentTags.includes(cfg.tag)) {
        await supabase.from("contacts").update({ tags: [...currentTags, cfg.tag] }).eq("id", contactId);
      }
      return `Added tag: "${cfg.tag}"`;
    }

    case "remove_tag": {
      const tags = (contact.tags || []).filter(t => t !== cfg.tag);
      await supabase.from("contacts").update({ tags }).eq("id", contactId);
      return `Removed tag: "${cfg.tag}"`;
    }

    case "notify_owner": {
      // Log as activity for now (could integrate with push notifications later)
      await supabase.from("activities").insert({
        org_id: orgId, contact_id: contactId,
        type: "notification", title: "Workflow notification",
        notes: merge(cfg.message || ""), user_id: context.ownerId || null,
      });
      return `Notified owner: "${merge(cfg.message || "")}"`;
    }

    case "slack_message": {
      // Log as activity (actual Slack integration would need webhook URL)
      await supabase.from("activities").insert({
        org_id: orgId, contact_id: contactId,
        type: "note", title: `Slack: ${cfg.channel || "#general"}`,
        notes: merge(cfg.message || ""),
        user_id: context.ownerId || null,
      });
      return `Would post to ${cfg.channel}: "${merge(cfg.message || "")}"`;
    }

    case "ai_summarize":
    case "ai_research":
    case "ai_draft": {
      // Log the AI action request — actual AI execution would call the AI endpoints.
      // user_id stays NULL (AI did it, not the rep) but properties.source='ai'
      // lets a future view surface these under a separate "AI activity" tab.
      await supabase.from("activities").insert({
        org_id: orgId, contact_id: contactId,
        type: "ai", title: `AI: ${step.label}`,
        notes: `Source: ${cfg.source || cfg.sources || "all"} → ${cfg.save_to || cfg.save_as || "notes"}`,
        user_id: null,
        properties: { source: "ai" },
      });
      return `Queued AI action: ${step.label}`;
    }

    case "webhook": {
      if (!cfg.url) throw new Error("No webhook URL configured");
      try {
        await fetch(cfg.url, {
          method: cfg.method || "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contact: { id: contactId, name: contactName, email: contactEmail }, event: context.triggerType, timestamp: new Date().toISOString() }),
        });
      } catch (e) { throw new Error(`Webhook failed: ${e.message}`); }
      return `Called ${cfg.method || "POST"} ${cfg.url}`;
    }

    default:
      return `Action "${step.action}" executed (no handler)`;
  }
}

function parseDueDays(dueStr) {
  if (dueStr?.includes("1 day")) return 1*24*60*60*1000;
  if (dueStr?.includes("2 day")) return 2*24*60*60*1000;
  if (dueStr?.includes("3 day")) return 3*24*60*60*1000;
  if (dueStr?.includes("5 day")) return 5*24*60*60*1000;
  if (dueStr?.includes("1 week")) return 7*24*60*60*1000;
  if (dueStr?.includes("2 week")) return 14*24*60*60*1000;
  return 2*24*60*60*1000; // default 2 days
}

export default { checkAndFireWorkflows };
